import { useEffect, useRef, useState } from 'react'

const POLL_INTERVAL_MS = 5 * 60 * 1000

async function fetchLiveCommit(): Promise<string | null> {
  try {
    const response = await fetch('/version.json', { cache: 'no-store' })
    if (!response.ok) return null
    const { commit }: { commit?: unknown } = await response.json()
    return typeof commit === 'string' ? commit : null
  } catch {
    // Offline, mid-deploy, or the SPA fallback answering with index.html: check again later.
    return null
  }
}

/**
 * Whether a different build of the app is live than the one this tab is running. The deploy
 * writes the live commit to /version.json; the build bakes its own into VITE_APP_COMMIT.
 * They're compared for difference, not order, so a rollback is picked up too.
 *
 * Once an update is found, the next change of `pathname` reloads the page, so the user lands
 * on the page they asked for in the new build. It never reloads on its own otherwise: the
 * user could be partway through a form.
 *
 * Checks on mount, when the tab becomes visible or focused, and every few minutes.
 *
 * Polls /version.json rather than the API: the API is deployed before the app, so it would
 * report the new commit while the old bundle is still being served.
 */
export function useAppUpdate(pathname: string): boolean {
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false)

  useEffect(() => {
    // Unset in local builds, which have no /version.json to compare against.
    const builtCommit: string | undefined = import.meta.env.VITE_APP_COMMIT
    if (!builtCommit || isUpdateAvailable) return

    let isCancelled = false
    const check = async () => {
      const liveCommit = await fetchLiveCommit()
      if (!isCancelled && liveCommit && liveCommit !== builtCommit) setIsUpdateAvailable(true)
    }
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') check()
    }

    check()
    const interval = window.setInterval(check, POLL_INTERVAL_MS)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('focus', check)
    return () => {
      isCancelled = true
      window.clearInterval(interval)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', check)
    }
  }, [isUpdateAvailable])

  const previousPathname = useRef(pathname)
  useEffect(() => {
    if (isUpdateAvailable && pathname !== previousPathname.current) window.location.reload()
    previousPathname.current = pathname
  }, [pathname, isUpdateAvailable])

  return isUpdateAvailable
}
